import * as XLSX from "xlsx";

const isFileLike = (value) =>
  Boolean(value && typeof value.arrayBuffer === "function");
const ORG_IMPORT_TEMPLATE_META_SHEET = "__nx_org_import_meta";
const LEVEL_REGEX = /^level_(\d+)$/;

const normalizeImportLevel = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  if (
    normalized === "level_1" ||
    normalized === "level 1" ||
    normalized === "1"
  )
    return "level_1";
  if (
    normalized === "level_2" ||
    normalized === "level 2" ||
    normalized === "2"
  )
    return "level_2";
  if (
    normalized === "level_3" ||
    normalized === "level 3" ||
    normalized === "3"
  )
    return "level_3";
  if (LEVEL_REGEX.test(normalized)) return normalized;
  return "";
};

const isFilledCell = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const toSheetRows = (sheet) =>
  XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: false,
  });

const getFilledHeaderColumns = (headerRow = []) =>
  headerRow
    .map((cell, index) => ({
      index,
      label: String(cell ?? "").trim(),
    }))
    .filter((entry) => entry.label.length > 0);

const sanitizePrimarySheet = (sheet) => {
  const rows = toSheetRows(sheet);
  if (rows.length === 0) {
    return { sheet, dataRows: 0 };
  }

  const header = Array.isArray(rows[0]) ? rows[0] : [];
  const filledColumns = getFilledHeaderColumns(header);
  if (filledColumns.length === 0) {
    return { sheet, dataRows: 0 };
  }

  const outputHeader = filledColumns.map((entry) => entry.label);
  const normalizedDataRows = rows
    .slice(1)
    .map((row = []) => filledColumns.map((entry) => row?.[entry.index] ?? null))
    .filter((row) => row.some(isFilledCell));

  const nextSheet = XLSX.utils.aoa_to_sheet([
    outputHeader,
    ...normalizedDataRows,
  ]);

  if (Array.isArray(sheet["!cols"])) {
    nextSheet["!cols"] = filledColumns.map(
      (entry) => sheet["!cols"]?.[entry.index],
    );
  }

  return { sheet: nextSheet, dataRows: normalizedDataRows.length };
};

const parseTemplateMeta = (workbook) => {
  const sheet = workbook?.Sheets?.[ORG_IMPORT_TEMPLATE_META_SHEET];
  if (!sheet) return null;

  const rows = toSheetRows(sheet);
  const kv = new Map();
  rows.forEach((row) => {
    const key = String(row?.[0] ?? "").trim();
    if (!key) return;
    kv.set(key, String(row?.[1] ?? "").trim());
  });

  return {
    templateType: kv.get("templateType") || "",
    templateVersion: kv.get("templateVersion") || "",
    companyId: kv.get("companyId") || "",
    level: normalizeImportLevel(kv.get("level") || ""),
    issuedAt: kv.get("issuedAt") || "",
    signature: kv.get("signature") || "",
  };
};

export const isSanitizedOrganizationImportPayload = (value) => {
  if (!value || typeof value !== "object") return false;
  return (
    Number.isFinite(value.dataRows) &&
    value.dataRows >= 0 &&
    isFileLike(value.file)
  );
};

export const sanitizeOrganizationImportFile = async (file) => {
  if (!isFileLike(file)) throw new Error("Excel file is required.");

  const inputBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(inputBuffer, { type: "array" });
  const sanitizedWorkbook = XLSX.utils.book_new();
  const templateMeta = parseTemplateMeta(workbook);

  const primarySheetName = workbook.SheetNames?.[0];
  let primaryDataRows = 0;

  workbook.SheetNames.forEach((name) => {
    const originalSheet = workbook.Sheets[name];
    if (name !== primarySheetName) {
      XLSX.utils.book_append_sheet(sanitizedWorkbook, originalSheet, name);
      return;
    }

    const { sheet, dataRows } = sanitizePrimarySheet(originalSheet);
    if (name === primarySheetName) primaryDataRows = dataRows;
    XLSX.utils.book_append_sheet(sanitizedWorkbook, sheet, name);
  });

  const outputBuffer = XLSX.write(sanitizedWorkbook, {
    type: "array",
    bookType: "xlsx",
  });

  const outputName =
    file.name?.replace(/\.(xlsx|xls)$/i, "") || "organization_import";
  const sanitizedFile = new File([outputBuffer], `${outputName}.xlsx`, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  return { file: sanitizedFile, dataRows: primaryDataRows, templateMeta };
};
